/**
 * Tajima DST.
 *
 * Layout: 512-byte ASCII header ("LA:", "ST:", "CO:", "+X:", "-X:", "+Y:", "-Y:", "AX:", "AY:",
 * "MX:", "MY:", "PD:", terminated by 0x1A and padded with spaces), followed by 3-byte records.
 * Each record encodes dx, dy in balanced ternary (±1, ±3, ±9, ±27, ±81 → max ±121 per axis).
 * Byte 2 always has bits 0 and 1 set. Flags in byte 2: 0x80 jump, 0xC0 colour change/stop,
 * 0xF3 end. DST's y axis points up, so y is negated on write.
 *
 * DST has no trim command and stores no colours. Trims are emitted as the widely used
 * convention of three consecutive jump records with zero net movement (+2, −4, +2), which Tajima
 * compatible machines interpret as a trim (the count is configurable on most machines).
 *
 * Reference: Tajima DST format as documented by the EmbroidePy project (pyembroidery, MIT);
 * this is an independent TypeScript implementation.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import { type FormatLimits, type MachinePattern, type MachineStitch, extents } from './machine';

export const DST_LIMITS: FormatLimits = { maxStitch: 121, maxJump: 121, maxCoord: 99999 };
export const DST_HEADER_SIZE = 512;
/** Number of jump records written for one trim. */
export const DST_TRIM_JUMPS = 3;

const bit = (b: number): number => 1 << b;

export type DstRecordType = 'stitch' | 'jump' | 'color' | 'end';

/** Encodes one record. dx/dy are in machine units with y pointing DOWN (flipped here). */
export function encodeDstRecord(dx: number, dyDown: number, type: DstRecordType): [number, number, number] {
  if (type === 'color') return [0, 0, 0xc3];
  if (type === 'end') return [0, 0, 0xf3];
  let x = dx;
  let y = -dyDown;
  if (!Number.isInteger(x) || !Number.isInteger(y) || Math.abs(x) > 121 || Math.abs(y) > 121) {
    throw new FormatError(t('DST displacement outside ±121: ({dx}, {dy})', { dx, dy: dyDown }));
  }
  let b0 = 0, b1 = 0, b2 = 0x03;
  if (type === 'jump') b2 |= bit(7);
  if (x > 40) { b2 |= bit(2); x -= 81; }
  if (x < -40) { b2 |= bit(3); x += 81; }
  if (x > 13) { b1 |= bit(2); x -= 27; }
  if (x < -13) { b1 |= bit(3); x += 27; }
  if (x > 4) { b0 |= bit(2); x -= 9; }
  if (x < -4) { b0 |= bit(3); x += 9; }
  if (x > 1) { b1 |= bit(0); x -= 3; }
  if (x < -1) { b1 |= bit(1); x += 3; }
  if (x > 0) { b0 |= bit(0); x -= 1; }
  if (x < 0) { b0 |= bit(1); x += 1; }
  if (y > 40) { b2 |= bit(5); y -= 81; }
  if (y < -40) { b2 |= bit(4); y += 81; }
  if (y > 13) { b1 |= bit(5); y -= 27; }
  if (y < -13) { b1 |= bit(4); y += 27; }
  if (y > 4) { b0 |= bit(5); y -= 9; }
  if (y < -4) { b0 |= bit(4); y += 9; }
  if (y > 1) { b1 |= bit(7); y -= 3; }
  if (y < -1) { b1 |= bit(6); y += 3; }
  if (y > 0) { b0 |= bit(7); y -= 1; }
  if (y < 0) { b0 |= bit(6); y += 1; }
  if (x !== 0 || y !== 0) throw new FormatError(t('Internal error in DST encoding'));
  return [b0, b1, b2];
}

const gb = (b: number, pos: number): number => (b >> pos) & 1;

export function decodeDstRecord(b0: number, b1: number, b2: number): { dx: number; dyDown: number; type: DstRecordType } {
  const dx =
    gb(b2, 2) * 81 - gb(b2, 3) * 81 + gb(b1, 2) * 27 - gb(b1, 3) * 27 + gb(b0, 2) * 9 - gb(b0, 3) * 9 +
    gb(b1, 0) * 3 - gb(b1, 1) * 3 + gb(b0, 0) - gb(b0, 1);
  const dyUp =
    gb(b2, 5) * 81 - gb(b2, 4) * 81 + gb(b1, 5) * 27 - gb(b1, 4) * 27 + gb(b0, 5) * 9 - gb(b0, 4) * 9 +
    gb(b1, 7) * 3 - gb(b1, 6) * 3 + gb(b0, 7) - gb(b0, 6);
  let type: DstRecordType = 'stitch';
  if ((b2 & 0xf3) === 0xf3) type = 'end';
  else if ((b2 & 0xc3) === 0xc3) type = 'color';
  else if ((b2 & 0x83) === 0x83) type = 'jump';
  return { dx, dyDown: 0 - dyUp, type };
}

const pad = (s: string, n: number): string => (s.length >= n ? s.slice(0, n) : s + ' '.repeat(n - s.length));
const num = (v: number, width: number): string => {
  const s = String(Math.abs(Math.round(v)));
  return ' '.repeat(Math.max(0, width - s.length)) + s;
};
const signed = (v: number, width: number): string => (v >= 0 ? '+' : '-') + num(v, width);

/** Letters that do not decompose to ASCII under NFKD (Ø, Æ, ß, …). */
const ASCII_LETTERS: Record<string, string> = { Æ: 'AE', æ: 'ae', Ø: 'O', ø: 'o', Œ: 'OE', œ: 'oe', ß: 'ss', Þ: 'Th', þ: 'th', Ð: 'D', ð: 'd', Đ: 'D', đ: 'd', Ł: 'L', ł: 'l', ı: 'i' };

/** Transliterates to printable ASCII: "Ørnen på Æsøya" → "Ornen pa AEsoya". Other characters are dropped. */
export function toAscii(name: string): string {
  return name
    .replace(/[ÆæØøŒœßÞþÐðĐđŁłı]/g, (c) => ASCII_LETTERS[c])
    .normalize('NFKD')
    .replace(/[^\x20-\x7e]/g, '');
}

/** Sanitizes a design name for 8-bit headers. */
export function asciiName(name: string, max: number): string {
  const n = toAscii(name).trim();
  return (n || 'Untitled').slice(0, max);
}

export function writeDst(p: MachinePattern): Uint8Array {
  const records: [number, number, number][] = [];
  let cx = 0, cy = 0;
  for (const s of p.stitches) {
    switch (s.cmd) {
      case CMD.STITCH:
      case CMD.JUMP:
        records.push(encodeDstRecord(s.x - cx, s.y - cy, s.cmd === CMD.STITCH ? 'stitch' : 'jump'));
        cx = s.x;
        cy = s.y;
        break;
      case CMD.TRIM:
        records.push(encodeDstRecord(2, 2, 'jump'), encodeDstRecord(-4, -4, 'jump'), encodeDstRecord(2, 2, 'jump'));
        break;
      case CMD.COLOR_CHANGE:
      case CMD.STOP:
        records.push(encodeDstRecord(0, 0, 'color'));
        break;
      case CMD.END:
        break;
    }
  }
  records.push(encodeDstRecord(0, 0, 'end'));

  const e = extents(p.stitches);
  const colorChanges = p.stitches.filter((s) => s.cmd === CMD.COLOR_CHANGE || s.cmd === CMD.STOP).length;
  const last = [...p.stitches].reverse().find((s) => s.cmd === CMD.STITCH || s.cmd === CMD.JUMP) ?? { x: 0, y: 0 };
  const w = new ByteWriter(DST_HEADER_SIZE + records.length * 3);
  w.ascii(`LA:${pad(asciiName(p.name, 16), 16)}\r`);
  w.ascii(`ST:${num(records.length - 1, 7)}\r`);
  w.ascii(`CO:${num(colorChanges, 3)}\r`);
  // DST extents use the y-up convention: +Y is the extent above the origin.
  w.ascii(`+X:${num(Math.max(0, e.maxX), 5)}\r`);
  w.ascii(`-X:${num(Math.max(0, -e.minX), 5)}\r`);
  w.ascii(`+Y:${num(Math.max(0, -e.minY), 5)}\r`);
  w.ascii(`-Y:${num(Math.max(0, e.maxY), 5)}\r`);
  w.ascii(`AX:${signed(last.x, 5)}\r`);
  w.ascii(`AY:${signed(-last.y, 5)}\r`);
  w.ascii(`MX:+${num(0, 5)}\r`);
  w.ascii(`MY:+${num(0, 5)}\r`);
  w.ascii(`PD:******\r`);
  w.u8(0x1a);
  w.fill(0x20, DST_HEADER_SIZE - w.pos);
  for (const r of records) w.bytes(r);
  return w.toBytes();
}

export interface DstFile {
  header: Record<string, string>;
  /** Raw records as encoded (trims appear as jump triplets). */
  records: { dx: number; dyDown: number; type: DstRecordType }[];
  /** Absolute positions; trims are reconstructed from zero-net jump triplets. */
  stitches: MachineStitch[];
}

export function readDst(bytes: Uint8Array): DstFile {
  if (bytes.length < DST_HEADER_SIZE) throw new FormatError(t('The DST file is shorter than its header (512 bytes).'));
  const header: Record<string, string> = {};
  const text = String.fromCharCode(...bytes.subarray(0, DST_HEADER_SIZE));
  const headerText = text.split('\x1a')[0];
  for (const line of headerText.split('\r')) {
    if (line.length > 3 && line[2] === ':') header[line.slice(0, 2)] = line.slice(3).trim();
  }
  if (!('LA' in header)) throw new FormatError(t('The DST header is missing the LA field.'));
  const r = new ByteReader(bytes);
  r.seek(DST_HEADER_SIZE);
  const records: DstFile['records'] = [];
  let ended = false;
  while (r.remaining >= 3) {
    const rec = decodeDstRecord(r.u8(), r.u8(), r.u8());
    records.push(rec);
    if (rec.type === 'end') {
      ended = true;
      break;
    }
  }
  if (!ended) throw new FormatError(t('The DST file is missing the end command (0xF3).'));
  const stitches: MachineStitch[] = [];
  let x = 0, y = 0;
  for (let i = 0; i < records.length; i++) {
    const rec = records[i];
    // trim triplet: (+2,+2) (-4,-4) (+2,+2) as jumps
    if (
      rec.type === 'jump' && rec.dx === 2 && rec.dyDown === 2 &&
      records[i + 1]?.type === 'jump' && records[i + 1].dx === -4 && records[i + 1].dyDown === -4 &&
      records[i + 2]?.type === 'jump' && records[i + 2].dx === 2 && records[i + 2].dyDown === 2
    ) {
      stitches.push({ x, y, cmd: CMD.TRIM });
      i += 2;
      continue;
    }
    x += rec.dx;
    y += rec.dyDown;
    const cmd = rec.type === 'stitch' ? CMD.STITCH : rec.type === 'jump' ? CMD.JUMP : rec.type === 'color' ? CMD.COLOR_CHANGE : CMD.END;
    stitches.push({ x, y, cmd });
  }
  return { header, records, stitches };
}
